import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Container,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Inventory,
  PlaylistAddCheck,
  Save,
  ErrorOutline
} from '@mui/icons-material';
import { purchaseAPI, racksAPI } from '../../services/API';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';

export const ReceivePurchase = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  
  const isPartial = searchParams.get('partial') === 'true';
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [batches, setBatches] = useState([]);
  const [racks, setRacks] = useState([]);
  const [receivedHistory, setReceivedHistory] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const steps = ['Review Order', 'Enter Batch Details', 'Confirmation'];

  useEffect(() => {
    fetchPurchaseOrder();
    fetchRacks();
  }, [id]);

  const fetchPurchaseOrder = async () => {
    try {
      const response = await purchaseAPI.getById(id);
      const order = response.data.data.purchaseOrder;
      setPurchaseOrder(order);

      // Extract received history from order items
      const history = [];
      order.items.forEach((item, index) => {
        if (item.receivedQuantity > 0) {
          history.push({
            itemIndex: index,
            medicineName: item.medicine.name,
            orderedQuantity: item.quantity,
            receivedQuantity: item.receivedQuantity,
            pendingQuantity: item.quantity - item.receivedQuantity
          });
        }
      });
      setReceivedHistory(history);

      // Initialize batches for each item
      const initialBatches = order.items.map((item, index) => ({
        itemIndex: index,
        batchNumber: generateBatchNumber(item.medicine),
        quantity: isPartial ? Math.min(1, item.quantity - item.receivedQuantity) : (item.quantity - item.receivedQuantity),
        expiryDate: '',
        manufactureDate: new Date().toISOString().split('T')[0],
        rack: '',
        orderedQuantity: Number(item.quantity),
        alreadyReceived: Number(item.receivedQuantity),
        maxReceivable: item.quantity - item.receivedQuantity
      }));

      setBatches(initialBatches);
    } catch (error) {
      enqueueSnackbar('Error fetching purchase order', { variant: 'error' });
      navigate('/hospital/pharmacy/purchases');
    } finally {
      setLoading(false);
    }
  };

  const fetchRacks = async () => {
    try {
      const response = await racksAPI.getAvailable();
      if (response.data.success) {
        setRacks(response.data.data.racks || []);
      } else {
        enqueueSnackbar('Error fetching racks', { variant: 'error' });
        setRacks([]);
      }
    } catch (error) {
      console.error('Error fetching racks:', error);
      enqueueSnackbar('Error fetching rack locations', { variant: 'error' });
      setRacks([]);
    }
  };

  const generateBatchNumber = (medicine) => {
    const prefix = medicine.name.substring(0, 3).toUpperCase();
    const timestamp = Date.now().toString().slice(-6);
    return `${prefix}-${timestamp}`;
  };

  const handleBatchChange = (index, field, value) => {
    const updatedBatches = [...batches];
    const batch = updatedBatches[index];
    
    if (field === 'quantity') {
      const numValue = Number(value) || 0;
      
      // Validate quantity doesn't exceed max receivable
      if (numValue > batch.maxReceivable) {
        enqueueSnackbar(`Cannot receive more than ${batch.maxReceivable} units`, { variant: 'warning' });
        value = batch.maxReceivable;
      }
      
      if (numValue < 0) {
        value = 0;
      }
    }
    
    updatedBatches[index] = {
      ...batch,
      [field]: field === 'quantity' ? Number(value) : value
    };
    
    setBatches(updatedBatches);
  };

  const validateBatches = () => {
    for (const batch of batches) {
      const item = purchaseOrder.items[batch.itemIndex];
      
      if (batch.quantity <= 0) {
        enqueueSnackbar(`Quantity must be greater than 0 for ${item.medicine.name}`, { variant: 'warning' });
        return false;
      }
      
      if (batch.quantity > batch.maxReceivable) {
        enqueueSnackbar(`Cannot receive more than ${batch.maxReceivable} units for ${item.medicine.name}`, { variant: 'warning' });
        return false;
      }
      
      if (!batch.batchNumber.trim()) {
        enqueueSnackbar('Batch number is required for all items', { variant: 'warning' });
        return false;
      }
      
      if (!batch.expiryDate) {
        enqueueSnackbar('Expiry date is required for all items', { variant: 'warning' });
        return false;
      }
      
      if (!batch.manufactureDate) {
        enqueueSnackbar('Manufacture date is required for all items', { variant: 'warning' });
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (activeStep === 1 && !validateBatches()) {
      return;
    }
    if (activeStep === 2) {
      setConfirmationOpen(true);
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleReceive = async () => {
    if (!validateBatches()) {
      return;
    }

    setSaving(true);
    try {
      const payload = {
        batches: batches
          .filter(batch => batch.quantity > 0)
          .map(batch => ({
            itemIndex: batch.itemIndex,
            batchNumber: batch.batchNumber,
            expiryDate: batch.expiryDate,
            manufactureDate: batch.manufactureDate,
            rack: batch.rack || undefined,
            quantity: isPartial ? Number(batch.quantity) : undefined
          }))
      };

      await purchaseAPI.receive(id, payload);
      
      enqueueSnackbar(
        `Purchase order ${isPartial ? 'partially received' : 'received'} successfully`,
        { variant: 'success' }
      );
      
      setConfirmationOpen(false);
      navigate('/hospital/pharmacy/purchases');
    } catch (error) {
      console.error('Receive error:', error);
      const errorMessage = error.response?.data?.message || 'Error receiving purchase order';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const calculateReceivedTotal = () => {
    return batches.reduce((total, batch) => {
      const item = purchaseOrder?.items[batch.itemIndex];
      if (item && batch.quantity > 0) {
        return total + (batch.quantity * item.costPrice);
      }
      return total;
    }, 0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography variant="h4">Loading...</Typography>
      </Box>
    );
  }

  if (!purchaseOrder) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography variant="h4">Purchase order not found</Typography>
      </Box>
    );
  }

  // Calculate overall progress
  const totalOrdered = purchaseOrder.items.reduce((sum, item) => sum + item.quantity, 0);
  const totalReceived = purchaseOrder.items.reduce((sum, item) => sum + item.receivedQuantity, 0);
  const totalPending = totalOrdered - totalReceived;
  const progressPercentage = totalOrdered > 0 ? (totalReceived / totalOrdered) * 100 : 0;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 5 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBack sx={{ fontSize: 28 }} />}
            onClick={() => navigate('/hospital/pharmacy/purchases')}
            sx={{ fontSize: '1.1rem' }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
              {isPartial ? 'Partially Receive' : 'Receive'} Purchase Order
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.2rem' }}>
              {purchaseOrder.poNumber} - {purchaseOrder.supplier.name}
            </Typography>
          </Box>
        </Box>
        
        <Chip
          icon={isPartial ? <PlaylistAddCheck sx={{ fontSize: 24 }} /> : <CheckCircle sx={{ fontSize: 24 }} />}
          label={isPartial ? 'Partial Receiving' : 'Complete Receiving'}
          color={isPartial ? 'warning' : 'success'}
          variant="outlined"
          sx={{ fontSize: '1.1rem', height: '36px' }}
        />
      </Box>

      {/* Receiving Progress */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.2rem' }}>
              Receiving Progress
            </Typography>
            <Typography variant="h6" color="primary" sx={{ fontSize: '1.2rem' }}>
              {progressPercentage.toFixed(1)}%
            </Typography>
          </Box>
          
          <Box sx={{ width: '100%', mb: 1 }}>
            <LinearProgress 
              variant="determinate" 
              value={progressPercentage} 
              sx={{ height: 10, borderRadius: 5 }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Ordered Quantity
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {totalOrdered} units
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="success.main">
                Already Received
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="success.main">
                {totalReceived} units
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color={totalPending > 0 ? 'warning.main' : 'text.secondary'}>
                Pending
              </Typography>
              <Typography variant="h6" fontWeight="bold" color={totalPending > 0 ? 'warning.main' : 'text.secondary'}>
                {totalPending} units
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                This Receiving
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {batches.reduce((sum, batch) => sum + (batch.quantity || 0), 0)} units
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Received History Alert */}
      {receivedHistory.length > 0 && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 3,
            fontSize: '1.1rem',
            '& .MuiAlert-message': { fontSize: '1.1rem' }
          }}
          icon={<Inventory sx={{ fontSize: 28 }} />}
        >
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ fontSize: '1.1rem' }}>
            Previous Receivings Found
          </Typography>
          <Typography sx={{ fontSize: '1rem' }}>
            Some items from this purchase order have already been received. 
            You can only receive the remaining quantities.
          </Typography>
        </Alert>
      )}

      {/* Stepper */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel sx={{ fontSize: '1.1rem' }}>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Step 1: Review Order */}
      {activeStep === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ py: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                  Order Items with Receiving Status
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Medicine</TableCell>
                        <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Ordered Qty</TableCell>
                        <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Already Received</TableCell>
                        <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Pending</TableCell>
                        <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Cost Price</TableCell>
                        <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {purchaseOrder.items.map((item, index) => {
                        const pending = item.quantity - item.receivedQuantity;
                        return (
                          <TableRow key={index}>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                                {item.medicine.name}
                              </Typography>
                              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                                {item.medicine.genericName} • {item.medicine.strength}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>
                              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                {item.quantity}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>
                              <Typography 
                                variant="h6" 
                                color={item.receivedQuantity > 0 ? "success.main" : "text.secondary"}
                                sx={{ fontSize: '1.1rem' }}
                              >
                                {item.receivedQuantity}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>
                              <Typography 
                                variant="h6" 
                                color={pending > 0 ? "warning.main" : "success.main"}
                                sx={{ fontSize: '1.1rem' }}
                              >
                                {pending}
                                {pending === 0 && (
                                  <CheckCircle sx={{ fontSize: 16, ml: 0.5, verticalAlign: 'middle' }} />
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>
                              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                ₹{item.costPrice}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 2 }}>
                              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                                ₹{(item.quantity * item.costPrice).toFixed(2)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ position: 'sticky', top: 100 }}>
              <CardContent sx={{ py: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                  Receiving Summary
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                    Order Status
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Chip
                      label={`${totalReceived}/${totalOrdered} received`}
                      color={totalPending === 0 ? "success" : "warning"}
                      size="medium"
                      sx={{ fontSize: '1rem' }}
                    />
                    {totalPending === 0 && (
                      <CheckCircle color="success" sx={{ fontSize: 20 }} />
                    )}
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 1 }}>
                    Receiving Capacity
                  </Typography>
                  {purchaseOrder.items.map((item, index) => {
                    const pending = item.quantity - item.receivedQuantity;
                    if (pending > 0) {
                      return (
                        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontSize: '1rem' }}>
                            {item.medicine.name.substring(0, 15)}...
                          </Typography>
                          <Typography variant="body2" color="primary" sx={{ fontSize: '1rem' }}>
                            Can receive up to {pending}
                          </Typography>
                        </Box>
                      );
                    }
                    return null;
                  })}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Alert 
                  severity={isPartial ? 'warning' : 'info'} 
                  sx={{ 
                    mb: 2,
                    fontSize: '1.1rem',
                    '& .MuiAlert-message': { fontSize: '1.1rem' }
                  }}
                >
                  {isPartial 
                    ? 'You can receive any quantity up to the pending amount for each item.'
                    : totalPending > 0 
                      ? 'You will receive all pending items in this batch.'
                      : 'All items have been received. No pending items to receive.'
                  }
                </Alert>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleNext}
                  disabled={totalPending === 0}
                  startIcon={<Inventory sx={{ fontSize: 24 }} />}
                  sx={{ 
                    py: 1.5,
                    fontSize: '1.1rem'
                  }}
                >
                  {totalPending > 0 ? 'Continue to Batch Details' : 'No Items to Receive'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Step 2: Batch Details */}
      {activeStep === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ py: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                  Enter Batch Details
                </Typography>
                
                <Alert 
                  severity="info" 
                  sx={{ 
                    mb: 3,
                    fontSize: '1.1rem',
                    '& .MuiAlert-message': { fontSize: '1.1rem' }
                  }}
                >
                  Enter the quantities you are receiving now. You can only receive up to the pending quantity for each item.
                </Alert>

                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Medicine</TableCell>
                        <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Ordered</TableCell>
                        <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Already Received</TableCell>
                        <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Max You Can Receive</TableCell>
                        <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Receiving Now</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Batch Number</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Manufacture Date</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Expiry Date</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Rack/Location</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {batches.map((batch, index) => {
                        const item = purchaseOrder.items[batch.itemIndex];
                        const maxReceivable = item.quantity - item.receivedQuantity;
                        
                        if (maxReceivable === 0) return null;
                        
                        return (
                          <TableRow key={index}>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                                {item.medicine.name}
                              </Typography>
                              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                                {item.medicine.genericName}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>
                              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                {item.quantity}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>
                              <Typography variant="h6" color="success.main" sx={{ fontSize: '1.1rem' }}>
                                {item.receivedQuantity}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>
                              <Typography variant="h6" color="warning.main" sx={{ fontSize: '1.1rem' }}>
                                {maxReceivable}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>
                              <TextField
                                type="number"
                                size="medium"
                                value={batch.quantity}
                                onChange={(e) => handleBatchChange(index, 'quantity', e.target.value)}
                                inputProps={{ 
                                  min: 1, 
                                  max: maxReceivable,
                                  style: { 
                                    textAlign: 'center',
                                    fontSize: '1.1rem'
                                  }
                                }}
                                sx={{ width: 100 }}
                                helperText={`Max: ${maxReceivable}`}
                                error={batch.quantity > maxReceivable}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <TextField
                                size="medium"
                                value={batch.batchNumber}
                                onChange={(e) => handleBatchChange(index, 'batchNumber', e.target.value)}
                                placeholder="Batch number"
                                sx={{ fontSize: '1.1rem' }}
                                InputProps={{ sx: { fontSize: '1.1rem' } }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <TextField
                                type="date"
                                size="medium"
                                value={batch.manufactureDate}
                                onChange={(e) => handleBatchChange(index, 'manufactureDate', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ fontSize: '1.1rem' }}
                                InputProps={{ sx: { fontSize: '1.1rem' } }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <TextField
                                type="date"
                                size="medium"
                                value={batch.expiryDate}
                                onChange={(e) => handleBatchChange(index, 'expiryDate', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                required
                                sx={{ fontSize: '1.1rem' }}
                                InputProps={{ sx: { fontSize: '1.1rem' } }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <FormControl size="medium" sx={{ minWidth: 140 }}>
                                <InputLabel sx={{ fontSize: '1.1rem' }}>Select Rack</InputLabel>
                                <Select
                                  value={batch.rack || ''}
                                  label="Select Rack"
                                  onChange={(e) => handleBatchChange(index, 'rack', e.target.value)}
                                  sx={{ fontSize: '1.1rem' }}
                                >
                                  <MenuItem value="" sx={{ fontSize: '1.1rem' }}>
                                    <em>None</em>
                                  </MenuItem>
                                  {racks.map((rack) => (
                                    <MenuItem key={rack._id} value={rack._id} sx={{ fontSize: '1.1rem' }}>
                                      {rack.name} {rack.location ? `(${rack.location.aisle}-${rack.location.row}-${rack.location.section})` : ''}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Receiving Summary */}
                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="h5" gutterBottom sx={{ fontSize: '1.2rem' }}>
                    Receiving Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Total Ordered
                      </Typography>
                      <Typography variant="h6">
                        {totalOrdered} units
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="success.main">
                        Already Received
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {totalReceived} units
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="warning.main">
                        Receiving Now
                      </Typography>
                      <Typography variant="h6" color="warning.main">
                        {batches.reduce((sum, batch) => sum + (batch.quantity || 0), 0)} units
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Total Value
                      </Typography>
                      <Typography variant="h6">
                        ₹{calculateReceivedTotal().toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                  <Button 
                    onClick={handleBack} 
                    variant="outlined"
                    sx={{ fontSize: '1.1rem', py: 1.5 }}
                  >
                    Back
                  </Button>
                  <Button 
                    onClick={handleNext} 
                    variant="contained"
                    disabled={batches.reduce((sum, batch) => sum + (batch.quantity || 0), 0) === 0}
                    sx={{ fontSize: '1.1rem', py: 1.5 }}
                  >
                    Review & Confirm
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Step 3: Confirmation */}
      {activeStep === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ py: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: '1.5rem', mb: 3 }}>
                  Confirmation
                </Typography>
                
                <Alert 
                  severity="success" 
                  sx={{ 
                    mb: 3,
                    fontSize: '1.1rem',
                    '& .MuiAlert-message': { fontSize: '1.1rem' }
                  }}
                  icon={<CheckCircle sx={{ fontSize: 28 }} />}
                >
                  Ready to {isPartial ? 'partially receive' : 'receive'} this order. Please review the details below.
                </Alert>

                {/* Receiving Progress Summary */}
                <Card sx={{ mb: 3, bgcolor: 'background.default' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontSize: '1.2rem' }}>
                      Receiving Progress After This Batch
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Current Received
                        </Typography>
                        <Typography variant="h5" color="success.main">
                          {totalReceived} units
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          This Receiving
                        </Typography>
                        <Typography variant="h5" color="primary">
                          + {batches.reduce((sum, batch) => sum + (batch.quantity || 0), 0)} units
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" color="text.secondary">
                          Will Be Received
                        </Typography>
                        <Typography variant="h5" fontWeight="bold">
                          {totalReceived + batches.reduce((sum, batch) => sum + (batch.quantity || 0), 0)}/{totalOrdered} units
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Medicine</TableCell>
                        <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Ordered</TableCell>
                        <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Already Received</TableCell>
                        <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Receiving Now</TableCell>
                        <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Will Be After</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Batch Number</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Expiry Date</TableCell>
                        <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', py: 2 }}>Rack Location</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {batches.map((batch, index) => {
                        const item = purchaseOrder.items[batch.itemIndex];
                        const quantity = batch.quantity;
                        const willBeAfter = item.receivedQuantity + quantity;
                        const selectedRack = racks.find(rack => rack._id === batch.rack);
                        
                        if (quantity === 0) return null;
                        
                        return (
                          <TableRow key={index}>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                                {item.medicine.name}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>
                              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                {item.quantity}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>
                              <Typography variant="h6" color="success.main" sx={{ fontSize: '1.1rem' }}>
                                {item.receivedQuantity}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>
                              <Typography variant="h6" color="primary" sx={{ fontSize: '1.1rem' }}>
                                {quantity}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2 }}>
                              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                                {willBeAfter}/{item.quantity}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                {batch.batchNumber}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                {new Date(batch.expiryDate).toLocaleDateString()}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                {selectedRack ? `${selectedRack.name} ${selectedRack.location ? `(${selectedRack.location})` : ''}` : 'Not assigned'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                  <Button 
                    onClick={handleBack} 
                    variant="outlined"
                    sx={{ fontSize: '1.1rem', py: 1.5 }}
                  >
                    Back
                  </Button>
                  <Button 
                    onClick={handleNext} 
                    variant="contained"
                    startIcon={<CheckCircle sx={{ fontSize: 24 }} />}
                    sx={{ fontSize: '1.1rem', py: 1.5 }}
                  >
                    {isPartial ? 'Partially Receive Order' : 'Receive Complete Order'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      </Container>

      {/* Confirmation Dialog */}
      <Dialog open={confirmationOpen} onClose={() => setConfirmationOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1.5rem' }}>
          Confirm {isPartial ? 'Partial' : 'Complete'} Receiving
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ 
            mb: 2,
            fontSize: '1.1rem',
            '& .MuiAlert-message': { fontSize: '1.1rem' }
          }}>
            This action will update inventory stock and cannot be undone.
          </Alert>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: 1 }}>
              Receiving Summary:
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body1" sx={{ fontSize: '1rem' }}>Already Received:</Typography>
                <Typography variant="body1" color="success.main" sx={{ fontSize: '1rem' }}>
                  {totalReceived} units
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body1" sx={{ fontSize: '1rem' }}>This Receiving:</Typography>
                <Typography variant="body1" color="primary" sx={{ fontSize: '1rem' }}>
                  {batches.reduce((sum, batch) => sum + (batch.quantity || 0), 0)} units
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1" sx={{ fontSize: '1rem' }}>Total Value:</Typography>
                <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                  ₹{calculateReceivedTotal().toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            Are you sure you want to {isPartial ? 'partially receive' : 'mark as received'} purchase order{' '}
            <strong>{purchaseOrder.poNumber}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmationOpen(false)}
            sx={{ fontSize: '1.1rem' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleReceive} 
            variant="contained" 
            disabled={saving}
            startIcon={saving ? null : <Save sx={{ fontSize: 24 }} />}
            sx={{ fontSize: '1.1rem' }}
          >
            {saving ? 'Processing...' : 'Confirm Receiving'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};